import { API_URL } from "../config";
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import AnimatedGrid from './AnimatedGrid';

const PricingSection = () => {
    const [isYearly, setIsYearly] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(null);

    const handleSubscribe = async (planType) => {
        if (!user) {
            navigate('/login');
            return;
        }

        if (planType === 'free') {
             navigate('/dashboard');
             return;
        }

        setLoading(planType);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_URL}/payments/initiate`, 
                { planType },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const { formParams, paytmUrl } = response.data;

            const form = document.createElement('form');
            form.method = 'POST';
            form.action = paytmUrl;

            for (const key in formParams) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = formParams[key];
                form.appendChild(input);
            }

            document.body.appendChild(form);
            form.submit();
            
        } catch (error) {
            console.error('Payment initiation failed:', error);
            alert('Failed to initiate payment. Please try again.');
            setLoading(null);
        }
    };

    const appPlans = [
        {
            id: 'free',
            name: 'Free Plan',
            priceLabel: 'Free',
            duration: '',
            features: [
                'Create up to 1 unlock link',
                'Standard Telegram forced subscriptions',
                'Basic visit & click stats',
                'Default link themes',
                'Standard support'
            ],
            buttonLabel: 'Get Started',
            buttonStyle: 'dark'
        },
        {
            id: 'standard',
            name: 'Standard Plan',
            priceLabel: isYearly ? '₹1499' : '₹150',
            duration: isYearly ? '/y' : '/m',
            planType: isYearly ? 'basic_yearly' : 'basic_monthly',
            features: [
                'Create up to 5 unlock links',
                'Advanced action locks',
                'Detailed analytics dashboard',
                'Custom link themes & colors',
                'Priority email support'
            ],
            buttonLabel: 'Get Started',
            buttonStyle: 'light'
        },
        {
            id: 'premium',
            name: 'Premium Plan',
            priceLabel: isYearly ? '₹1999' : '₹299',
            duration: isYearly ? '/y' : '/m',
            planType: isYearly ? 'premium_yearly' : 'premium_monthly',
            features: [
                'Unlimited unlock links',
                'All forced subscription types',
                'Real-time comprehensive analytics',
                'Custom background uploads (up to 5MB)',
                '24/7 priority support'
            ],
            buttonLabel: 'Get Started',
            buttonStyle: 'dark'
        }
    ];

    return (
        <section id="pricing" className="relative w-full pt-24 pb-12 px-4 max-w-7xl mx-auto flex flex-col items-center">
            <div className="absolute inset-0 z-0">
                <AnimatedGrid />
            </div>

            {}
            <div className="absolute top-16 md:top-24 left-0 right-0 w-full text-center flex justify-center pointer-events-none select-none z-10">
                <h2 className="text-[120px] sm:text-[180px] md:text-[240px] lg:text-[280px] font-black tracking-tighter text-white opacity-[0.9] leading-none" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                    Pricing
                </h2>
                {}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505] opacity-50"></div>
            </div>

            <div className="w-full flex flex-col lg:flex-row items-center lg:items-stretch justify-center gap-6 relative z-20 mt-8 md:mt-32 lg:mt-40">
                {appPlans.map((plan, index) => (
                    <motion.div 
                        key={plan.id}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="w-full max-w-[340px] flex flex-col bg-[#0f0f0f]/50 backdrop-blur-[20px] border border-white/[0.08] rounded-[24px] p-8 shadow-2xl"
                    >
                        <div className="mb-10">
                            <h3 className="text-gray-400 text-sm font-medium mb-4">{plan.name}</h3>
                            <div className="flex items-baseline text-white">
                                <span className="text-[44px] font-bold tracking-tight leading-none">{plan.priceLabel}</span>
                                {plan.duration && <span className="text-2xl text-gray-400 ml-1 font-medium">{plan.duration}</span>}
                            </div>
                        </div>

                        <ul className="flex-1 space-y-5 mb-12">
                            {plan.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start text-[13px] text-gray-400 leading-tight">
                                    <div className="flex-shrink-0 w-4 h-4 rounded-full bg-white/10 flex items-center justify-center mr-4 mt-0.5">
                                        <Check size={10} className="text-white" />
                                    </div>
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>

                        <button
                            onClick={() => handleSubscribe(plan.planType || 'free')}
                            disabled={loading === plan.planType}
                            className={`w-full py-4 rounded-full text-[14px] font-semibold transition-all duration-300 shadow-lg
                                ${plan.buttonStyle === 'light' 
                                    ? 'bg-white text-black hover:bg-gray-100' 
                                    : 'bg-[#0a0a0a] border border-white/10 text-white hover:bg-[#1a1a1a]'
                                }
                                ${loading === plan.planType ? 'opacity-50 cursor-not-allowed' : ''}
                            `}
                        >
                            {loading === plan.planType ? 'Processing...' : plan.buttonLabel}
                        </button>
                    </motion.div>
                ))}
            </div>

            {}
            <div className="mt-16 w-full max-w-[1060px] flex items-center justify-start gap-3 px-4 z-20 relative">
                <button 
                    onClick={() => setIsYearly(!isYearly)}
                    className="w-11 h-[22px] rounded-full bg-white/10 flex items-center p-1 cursor-pointer transition-colors relative border border-white/5"
                >
                    <motion.div 
                        className="w-3.5 h-3.5 rounded-full bg-white absolute"
                        animate={{ left: isYearly ? '25px' : '4px' }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                </button>
                <span className="text-[13px] text-gray-400">Billed Yearly</span>
            </div>

        </section>
    );
};

export default PricingSection;
